import { Grid, Autocomplete, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Person as PersonIcon, PersonAdd as PersonAddIcon } from "@mui/icons-material";
import { useSearch, Search, useEntityFunctions } from "@emberly/zenith-client";
import { useTranslation } from "react-i18next";
import EntityEditDialog from "./EntityEditDialog";
import { getTextSearch } from "../../../common/search";
import useContactFields from "../../../hooks/useContactFields";

const ContactQuery = (inputValue, customer) => !!customer ? {
  must: [
    getTextSearch("name", inputValue),
    {
      path: "isCustomer",
      function: Search.EQUALS,
      value: true
    },
  ]
} : getTextSearch("name", inputValue);

const TEXTFIELD_SX = {
  "&:hover": {
    backgroundColor: "transparent"
  }
};

const ADD_STYLE = { borderBottom: `1px solid rgba(0,0,0,0.125)` };

const GetOptionLabel = (option) => typeof option === 'string' ? option : option.name;

const IsOptionEqualToValue = (a, b) => a.id === b.id;


export default function ContactPicker(props) {
  const { label, value, onChange, disabled, customer, initialValues, fetchInitialValues } = props;
  const [create, setCreate] = useState(false);
  const { t } = useTranslation();
  const { createEntity } = useEntityFunctions("Contact");

  const [defaultValues, setDefaultValues] = useState({});
  const [popupOpen, setPopupOpen] = useState(false);
  const [option, setOption] = useState(value || null);
  const [inputValue, setInputValue] = useState("");
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const valueRef = useRef(value);
  valueRef.current = value;

  if (!!value && !option) {
    setOption(value);
  } else if (!!value && !!option && (value.id !== option.id || value.name !== option.name)) {
    setOption(value);
  }

  useEffect(() => {
    if (popupOpen) {

      const timer = setTimeout(
        () => setSearchQuery(inputValue),
        250
      );

      return () => {
        clearTimeout(timer);
      }
    }
  }, [inputValue, popupOpen]);

  const onPopupOpen = useCallback(() => {
    setSearchQuery(inputValue);
    setPopupOpen(true);
  }, [inputValue]);

  const filterQuery = useMemo(() => {
    return ContactQuery(searchQuery, customer);
  }, [customer, searchQuery]);

  const { entities } = useSearch("Contact", filterQuery, popupOpen);

  const filteredEntities = useMemo(() => {
    const result = customer ? entities.filter(t => !!t.billing && !!t.billing.address && !!t.billing.postNumber && !!t.billing.country && !!t.billing.area) : entities;
    if (popupOpen && (!!inputValue || !!valueRef.current?.name) && !option?.id) {
      return [{ name: `${t("contactRegister:addPre")} "${inputValue || valueRef.current?.name || ""}" ${t("contactRegister:addPost")}`, isAdd: true, id: "add-new-contact" }, ...result];
    } else {
      return result;
    }
  }, [entities, customer, popupOpen, inputValue, t, option]);

  const onBeginCreate = useCallback(ev => {
    ev.preventDefault();
    let iv = typeof fetchInitialValues === "function" ? fetchInitialValues() : initialValues;
    setDefaultValues(!!iv ? {
      name: inputValue || valueRef.current?.name || "",
      isCustomer: customer,
      location: iv.location,
      email: iv.email,
      phoneNumber: iv.phoneNumber,
      billing: customer ? {
        country: iv.location?.country?.length === 2 ? iv.location.country : "NO",
        address: iv.location?.address || "",
        postNumber: iv.location?.postCode || "",
        area: iv.location?.place || "",
        email: iv.email || ""
      } : undefined
    } : { name: inputValue, isCustomer: customer });
    setCreate(true);
  }, [customer, inputValue, initialValues, fetchInitialValues])

  const onOptionSelected = useCallback((ev, option) => {
    if (!!option) {
      setOption(option);
      onChange(inputValue, option);
    } else {
      onChange(inputValue, null);
    }
  }, [onChange, inputValue]);

  const onInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
    onChange(newInputValue, null);
  }, [onChange]);

  const onPopupClose = useCallback(() => setPopupOpen(false), []);

  const onCreateEntity = useCallback(async (entityData) => {
    try {
      setCreating(true);
      if (!!entityData) {
        const createdEntity = await createEntity(entityData);
        if (!!createdEntity) {
          setOption(createdEntity);
          onChange(createdEntity.name, createdEntity);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setCreating(false);
      setCreate(false);
      setDefaultValues(null);
    }
  }, [createEntity, onChange]);

  const fields = useContactFields();

  return (<>
    <Autocomplete
      getOptionLabel={GetOptionLabel}
      isOptionEqualToValue={IsOptionEqualToValue}
      options={filteredEntities}
      autoComplete
      fullWidth
      onOpen={onPopupOpen}
      onClose={onPopupClose}
      freeSolo
      filterOptions={(x) => x}
      includeInputInList
      value={option}
      disabled={disabled || creating}
      noOptionsText={t("common:noResults")}
      onChange={onOptionSelected}
      onInputChange={onInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          variant="filled"
          fullWidth
          sx={TEXTFIELD_SX}
          slotProps={{
            input: {
              ...params.InputProps,
              disableUnderline: true,
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        return option.isAdd ? (
          <li {...props} key={option.id} onClick={onBeginCreate} style={ADD_STYLE}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <PersonAddIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                <Typography>
                  {option.name}
                </Typography>
              </Grid>
            </Grid>
          </li>
        ) : (
          <li {...props} key={option.id}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <PersonIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                <Typography>
                  {option.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {option?.email} {!!option?.email && !!option?.location?.place ? "-" : ""} {option.location?.place || ""}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
    {
      create ? (
        <EntityEditDialog
          createTitle={t("contactRegister:create")}
          fields={fields}
          entity={defaultValues}
          open
          onCancel={() => setCreate(false)}
          onCreate={onCreateEntity}
        />
      ) : null
    }
  </>);
}
